import uniq from 'lodash/uniq'

import { Control } from '@/gen/inventory/v1/control_service_pb'

import { controlStatusLabel } from '@/const/label'

import type { ControlRow } from '@/pages/control/ag-grid-control/types'

import type { GroupHeaderExtra } from './group-controls-by-key'
import { groupControlsByKey } from './group-controls-by-key'

const NO_SCOPE_LABEL = 'No scope'

/**
 * Groups controls by scope (scope.name for display) and creates group header rows with filtering metadata.
 * Uses control.scope (id, name) for grouping and display so the visible name is shown.
 *
 * This function:
 * 1. Sorts controls by scope name, then by title within each scope
 * 2. Groups controls by scope name (scope?.name)
 * 3. Creates scope header rows with metrics and filter values
 * 4. Adds child control rows only for expanded scopes
 *
 * @param controls - Array of controls to group (caller should pre-filter if needed)
 * @param expandedGroups - Set of scope names (visible names) that are expanded
 * @returns Array of ControlRow objects containing both group headers and control rows
 */
export function groupControlsByScope(
  controls: Control[],
  expandedGroups: Set<string>,
): ControlRow[] {
  const getGroupKey = (c: Control) => c.scope?.name ?? NO_SCOPE_LABEL

  return groupControlsByKey(controls, expandedGroups, {
    getGroupKey,
    headerIdPrefix: 'scope-',
    buildHeaderExtra(_groupKey, groupControls): GroupHeaderExtra {
      const scopeScopes = uniq(
        groupControls.map((c) => c.scope?.name).filter((s): s is string => Boolean(s)),
      )
      const scopeStatuses = uniq(
        groupControls
          .map((c) => (c.status !== undefined ? controlStatusLabel[c.status] : undefined))
          .filter((s): s is string => Boolean(s)),
      )
      const scopeSmartQuestionStatuses = uniq(
        groupControls
          .map((c) => c.smartQuestion?.status)
          .filter((s): s is number => s !== undefined && s !== null),
      )
      const scopeFromFirst = groupControls[0].scope
      const scope = scopeFromFirst ?? { id: 'no-scope', name: NO_SCOPE_LABEL }
      return {
        scope,
        categoryFilterValues: {
          scopes: scopeScopes,
          statuses: scopeStatuses,
          smartQuestionStatuses: scopeSmartQuestionStatuses,
        },
      }
    },
  })
}
